import React, { useState, useCallback, useRef, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import './TTSComparePage.css';

interface WordEntry {
  word: string;
  phonetic: string;
  audioUrl: string; // existing audio URL from ECDICT
}

interface InworldResult {
  audioUrl: string;
  latencyMs: number;
  cached: boolean;
}

interface WordRating {
  clarity: number;
  naturalness: number;
}

interface AggregatedStats {
  total: number;
  aggregated: { voice: string; count: number; avgClarity: number; avgNaturalness: number }[];
}

const VOICE_OPTIONS = ['Dennis', 'Ashley', 'Kira', 'Elias', 'Lily', 'Marcus'];

const StarRating: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
}> = ({ label, value, onChange }) => (
  <div className="tts-star-rating">
    <span className="tts-star-label">{label}</span>
    <div className="tts-stars">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          className={`tts-star ${star <= value ? 'tts-star--filled' : ''}`}
          onClick={() => onChange(star)}
          title={`${star} star${star > 1 ? 's' : ''}`}
        >
          ★
        </button>
      ))}
    </div>
  </div>
);

const TTSComparePage: React.FC = () => {
  const [words, setWords] = useState<WordEntry[]>([]);
  const [inworldResults, setInworldResults] = useState<Record<string, InworldResult>>({});
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState<Record<string, boolean>>({});
  const [generateAllRunning, setGenerateAllRunning] = useState(false);
  const [voiceId, setVoiceId] = useState('Dennis');
  const [speakingRate, setSpeakingRate] = useState(1.0);
  const [temperature, setTemperature] = useState(0.8);
  const [playingAudio, setPlayingAudio] = useState<string | null>(null);
  const [ratings, setRatings] = useState<Record<string, WordRating>>({});
  const [aggStats, setAggStats] = useState<AggregatedStats | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const fetchWords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/dictionary/tts-compare/words?count=200`);
      const data = await res.json();
      const parsed = (Array.isArray(data) ? data : []).map((w: any) => {
        let audioUrl = w.audioUrl || '';
        try {
          const arr = JSON.parse(audioUrl);
          if (Array.isArray(arr) && arr.length > 0) {
            audioUrl = `/uploads/${arr[0].path}`;
          }
        } catch {
          // already a plain URL or empty
        }
        return { word: w.word, phonetic: w.phonetic || '', audioUrl };
      });
      setWords(parsed);
      setInworldResults({});
      setRatings({});
    } catch (err) {
      console.error('Failed to fetch words:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchRatings = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/dictionary/tts-compare/ratings`);
      const data = await res.json();
      setAggStats(data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchWords();
    fetchRatings();
  }, [fetchWords, fetchRatings]);

  const playAudio = useCallback((url: string, key: string) => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (playingAudio === key) {
      setPlayingAudio(null);
      return;
    }
    const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
    const audio = new Audio(fullUrl);
    audioRef.current = audio;
    setPlayingAudio(key);
    audio.onended = () => { setPlayingAudio(null); audioRef.current = null; };
    audio.onerror = () => { setPlayingAudio(null); audioRef.current = null; };
    audio.play().catch(() => { setPlayingAudio(null); audioRef.current = null; });
  }, [playingAudio]);

  const generateOne = useCallback(async (word: string) => {
    setGenerating(prev => ({ ...prev, [word]: true }));
    try {
      const res = await fetch(`${API_BASE_URL}/dictionary/tts-compare/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ word, voiceId, speakingRate, temperature }),
      });
      const data = await res.json();
      if (data.audioUrl) {
        setInworldResults(prev => ({
          ...prev,
          [word]: { audioUrl: data.audioUrl, latencyMs: data.latencyMs, cached: data.cached },
        }));
      }
    } catch (err) {
      console.error(`Failed to generate TTS for ${word}:`, err);
    } finally {
      setGenerating(prev => ({ ...prev, [word]: false }));
    }
  }, [voiceId, speakingRate, temperature]);

  const generateAll = useCallback(async () => {
    setGenerateAllRunning(true);
    for (const w of words) {
      if (!inworldResults[w.word]) {
        await generateOne(w.word);
      }
    }
    setGenerateAllRunning(false);
  }, [words, inworldResults, generateOne]);

  const submitRating = useCallback(async (word: string, dimension: 'clarity' | 'naturalness', value: number) => {
    const current = ratings[word] || { clarity: 0, naturalness: 0 };
    const updated = { ...current, [dimension]: value };
    setRatings(prev => ({ ...prev, [word]: updated }));

    // Only submit when both dimensions are rated
    if (updated.clarity > 0 && updated.naturalness > 0) {
      try {
        await fetch(`${API_BASE_URL}/dictionary/tts-compare/rate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            word,
            voiceId,
            speakingRate,
            temperature,
            clarity: updated.clarity,
            naturalness: updated.naturalness,
          }),
        });
        fetchRatings();
      } catch (err) {
        console.error('Failed to submit rating:', err);
      }
    }
  }, [ratings, voiceId, speakingRate, temperature, fetchRatings]);

  // Local stats
  const generatedCount = Object.keys(inworldResults).length;
  const totalLatency = Object.values(inworldResults).reduce((s, r) => s + r.latencyMs, 0);
  const avgLatency = generatedCount > 0 ? Math.round(totalLatency / generatedCount) : 0;
  const ratedCount = Object.values(ratings).filter(r => r.clarity > 0 && r.naturalness > 0).length;
  const localRatings = Object.values(ratings).filter(r => r.clarity > 0 && r.naturalness > 0);
  const avgClarity = localRatings.length > 0
    ? (localRatings.reduce((s, r) => s + r.clarity, 0) / localRatings.length).toFixed(1)
    : '-';
  const avgNaturalness = localRatings.length > 0
    ? (localRatings.reduce((s, r) => s + r.naturalness, 0) / localRatings.length).toFixed(1)
    : '-';

  return (
    <div className="tts-compare-page">
      <div className="tts-compare-header">
        <h1>Dictionary TTS Comparison</h1>
        <div className="tts-compare-controls">
          <label>
            Voice:
            <select value={voiceId} onChange={e => setVoiceId(e.target.value)}>
              {VOICE_OPTIONS.map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </label>
          <label className="tts-slider-label">
            Rate: {speakingRate.toFixed(2)}
            <input
              type="range"
              className="tts-slider"
              min={0.8}
              max={1.2}
              step={0.05}
              value={speakingRate}
              onChange={e => setSpeakingRate(parseFloat(e.target.value))}
            />
          </label>
          <label className="tts-slider-label">
            Temp: {temperature.toFixed(1)}
            <input
              type="range"
              className="tts-slider"
              min={0.3}
              max={1.0}
              step={0.1}
              value={temperature}
              onChange={e => setTemperature(parseFloat(e.target.value))}
            />
          </label>
          <button
            className="tts-compare-btn tts-compare-btn--primary"
            onClick={fetchWords}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Refresh Words'}
          </button>
          <button
            className="tts-compare-btn tts-compare-btn--green"
            onClick={generateAll}
            disabled={generateAllRunning || words.length === 0}
          >
            {generateAllRunning ? 'Generating...' : 'Generate All'}
          </button>
        </div>
      </div>

      {loading && <div className="tts-compare-loading">Loading random words...</div>}

      <div className="tts-compare-list">
        {words.map((w, idx) => {
          const iw = inworldResults[w.word];
          const isGenerating = generating[w.word];
          const wordRating = ratings[w.word];
          return (
            <div key={w.word} className="tts-word-row">
              <span className="tts-word-index">{idx + 1}.</span>

              <div className="tts-word-info">
                <span className="tts-word-text">{w.word}</span>
                {w.phonetic && (
                  <span className="tts-word-phonetic"> /{w.phonetic}/</span>
                )}
              </div>

              {/* Existing audio */}
              <div className="tts-audio-group">
                <span className="tts-audio-label">Existing:</span>
                <button
                  className={`tts-play-btn ${playingAudio === `existing-${w.word}` ? 'tts-play-btn--playing' : ''}`}
                  onClick={() => playAudio(w.audioUrl, `existing-${w.word}`)}
                  title="Play existing audio"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </button>
              </div>

              {/* Inworld audio */}
              <div className="tts-audio-group">
                <span className="tts-audio-label">Inworld:</span>
                {iw ? (
                  <button
                    className={`tts-play-btn ${playingAudio === `inworld-${w.word}` ? 'tts-play-btn--playing' : ''}`}
                    onClick={() => playAudio(iw.audioUrl, `inworld-${w.word}`)}
                    title="Play Inworld audio"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </button>
                ) : (
                  <button
                    className="tts-generate-btn"
                    onClick={() => generateOne(w.word)}
                    disabled={isGenerating || generateAllRunning}
                  >
                    {isGenerating ? 'Gen...' : 'Generate'}
                  </button>
                )}
              </div>

              <span className="tts-latency">
                {iw ? (iw.cached ? 'cached' : `${iw.latencyMs}ms`) : ''}
              </span>

              {/* Rating area - shown after Inworld audio is generated */}
              {iw && (
                <div className="tts-rating-area">
                  <StarRating
                    label="Clarity"
                    value={wordRating?.clarity || 0}
                    onChange={v => submitRating(w.word, 'clarity', v)}
                  />
                  <StarRating
                    label="Natural"
                    value={wordRating?.naturalness || 0}
                    onChange={v => submitRating(w.word, 'naturalness', v)}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      {words.length > 0 && (
        <div className="tts-compare-footer">
          <div className="tts-compare-stats">
            <span>Generated: <strong>{generatedCount}/{words.length}</strong></span>
            {generatedCount > 0 && (
              <span>Avg Latency: <strong>{avgLatency}ms</strong></span>
            )}
            <span>Rated: <strong>{ratedCount}</strong></span>
            {ratedCount > 0 && (
              <>
                <span>Avg Clarity: <strong>{avgClarity}</strong></span>
                <span>Avg Naturalness: <strong>{avgNaturalness}</strong></span>
              </>
            )}
          </div>
          {aggStats && aggStats.aggregated.length > 0 && (
            <div className="tts-compare-agg">
              <strong>All-time ratings:</strong>
              {aggStats.aggregated.map(a => (
                <span key={a.voice} className="tts-agg-voice">
                  {a.voice}: {a.count} rated, clarity {a.avgClarity}, natural {a.avgNaturalness}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TTSComparePage;
